import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api, { eur } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const MAX_ATTEMPTS = 6;

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | success | failed | timeout
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let attempts = 0;
    let active = true;

    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          setOrder(data.order);
          setState("success");
          if (!cleared.current) {
            clear();
            cleared.current = true;
          }
          return;
        }
        if (data.status === "expired") {
          setState("failed");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setState("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setState("failed");
      }
    };
    poll();
    return () => {
      active = false;
    };
  }, [sessionId, clear]);

  return (
    <div className="px-6 md:px-12 py-20 md:py-28 max-w-2xl mx-auto text-center min-h-[60vh] flex flex-col items-center justify-center">
      {state === "checking" && (
        <div data-testid="order-checking">
          <Loader2 className="h-12 w-12 text-gold animate-spin mx-auto" strokeWidth={1.4} />
          <h1 className="font-serif text-3xl text-ink mt-6">Confirmation du paiement...</h1>
          <p className="text-graphite mt-3">Merci de patienter quelques instants.</p>
        </div>
      )}

      {state === "success" && (
        <div data-testid="order-success" className="animate-fade-up">
          <CheckCircle2 className="h-16 w-16 text-gold mx-auto" strokeWidth={1.2} />
          <h1 className="font-serif text-4xl md:text-5xl text-ink mt-6 tracking-tight">Merci pour votre commande !</h1>
          <p className="text-graphite mt-4">
            Votre paiement a été confirmé. Un email récapitulatif vous sera envoyé.
          </p>
          {order && (
            <div className="bg-sand rounded-sm p-6 mt-8 text-left">
              <p className="text-xs uppercase tracking-[0.15em] text-ash mb-3">Commande #{order.id?.slice(0, 8)}</p>
              {order.items?.map((i) => (
                <div key={i.product_id} className="flex justify-between text-sm py-1.5">
                  <span className="text-ink">{i.name} × {i.quantity}</span>
                  <span className="font-mono text-graphite">{eur(i.line_total)}</span>
                </div>
              ))}
              <div className="flex justify-between border-t border-stone mt-3 pt-3 font-mono text-ink">
                <span>Total</span>
                <span data-testid="success-total">{eur(order.total)}</span>
              </div>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <Link to="/compte" className="bg-ink text-cream px-8 py-4 text-xs uppercase tracking-[0.15em] hover:bg-gold hover:text-ink transition-colors">
              Suivre ma commande
            </Link>
            <Link to="/boutique" className="border border-ink text-ink px-8 py-4 text-xs uppercase tracking-[0.15em] hover:bg-ink hover:text-cream transition-colors">
              Continuer mes achats
            </Link>
          </div>
        </div>
      )}

      {(state === "failed" || state === "timeout") && (
        <div data-testid="order-failed">
          <XCircle className="h-14 w-14 text-destructive mx-auto" strokeWidth={1.3} />
          <h1 className="font-serif text-3xl text-ink mt-6">
            {state === "timeout" ? "Vérification en cours" : "Paiement non confirmé"}
          </h1>
          <p className="text-graphite mt-3">
            {state === "timeout"
              ? "Le paiement prend plus de temps que prévu. Consultez votre espace client dans quelques minutes."
              : "Le paiement n'a pas pu être confirmé. Veuillez réessayer."}
          </p>
          <Link to="/checkout" className="inline-block mt-8 bg-ink text-cream px-8 py-4 text-xs uppercase tracking-[0.15em]">
            Retour au panier
          </Link>
        </div>
      )}
    </div>
  );
}
